import { StateCreator } from "zustand";
import { ListenersSlice } from "@/stores/listeners.slice";
import { PerformanceStats } from "@/types/v2/AppAnalytics";
import { localStorageManager } from "@/services/localStorage";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";
import { APP_ANALYTICS_DELAY_MINS } from "@/lib/const";
import { ShopSlice } from "@/stores/shop.slice";
import { AppAnalyticsRepository } from "@/repositories/appAnalytics";

export interface AnalyticsSlice {
  performance: PerformanceStats;
  lastSentTimestamp: number;
  setPerformance: (performance: PerformanceStats) => void;
  setLastSentTimestamp: (timestamp: number) => void;
  sendAnalytics: () => Promise<void>;
}

const initialSlice: Omit<
  AnalyticsSlice,
  "setPerformance" | "setLastSentTimestamp" | "sendAnalytics"
> = {
  performance: {
    memory: { used: 0, total: 0 },
    fps: 0,
  },
  lastSentTimestamp: parseInt(
    localStorageManager.getItem(LOCAL_STORAGE_BASE_KEYS.ANALYTICS_TIMESTAMP) ||
      "0"
  ),
};

export const createAnalyticsSlice: StateCreator<
  AnalyticsSlice & ListenersSlice & ShopSlice,
  [],
  [],
  AnalyticsSlice
> = (set, get) => ({
  ...initialSlice,
  setPerformance: (performance) => {
    set({ performance });
  },
  setLastSentTimestamp: (timestamp) => {
    set({ lastSentTimestamp: timestamp });
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.ANALYTICS_TIMESTAMP,
      timestamp.toString()
    );
  },
  sendAnalytics: async () => {
    const { performance, lastSentTimestamp } = get();

    const shopId = get().shop?.id;
    if (!shopId) {
      return;
    }
    const now = new Date();
    const diff = now.getTime() - lastSentTimestamp;
    const diffInSeconds = Math.floor(diff / 1000);
    const shouldSendAnalytics = diffInSeconds > APP_ANALYTICS_DELAY_MINS * 60;

    // Only send analytics if at least 1 hour has passed since the last send
    if (shouldSendAnalytics) {
      const nCards = get().cards.length;
      const nTimers = Object.keys({}).length;
      const nEmployees = get().employees?.length ?? 0;
      const nActivityFeed = get().activityFeed.length;
      const nConversations = get().conversations.length;
      const nTags = get().tags ? 1 : 0;
      await AppAnalyticsRepository.logAnalytics(
        shopId,
        nCards + nTimers + nEmployees + nActivityFeed + nConversations + nTags,
        {
          cards: nCards,
          timers: nTimers,
          employees: nEmployees,
          activityFeed: nActivityFeed,
          conversations: nConversations,
          other: nTags,
        },
        performance
      );

      const now = Date.now();
      get().setLastSentTimestamp(now);
    }
  },
});
